// ── Supabase Auth — production implementation ─────────────────────────────
// Handles: signUp, signIn, signOut, password reset, session, profile load
// Auth trigger (in DB) auto-creates user_core + user_profiles + user_scores

import { supabase, db, dbUpdate } from './supabase';

// ── Error message mapping ─────────────────────────────────────────────────
function mapError(msg) {
  if (!msg) return 'Unknown error';
  if (msg.includes('already registered') || msg.includes('already been registered'))
    return 'auth.error_email_taken';
  if (msg.includes('Invalid login') || msg.includes('Invalid credentials'))
    return 'auth.error_invalid_credentials';
  if (msg.includes('Email not confirmed'))
    return 'auth.error_email_not_confirmed';
  if (msg.includes('Password should be'))
    return 'auth.error_password_weak';
  if (msg.includes('rate limit'))
    return 'auth.error_rate_limit';
  return msg;
}

// ── Sign up (email + password) ────────────────────────────────────────────
// The DB trigger handle_new_auth_user() auto-creates the 3 profile rows.
// We then update them with the onboarding data.
export async function registerUser({
  email, password, name, age, gender, seeking,
  dobDay, dobMonth, dobYear, networkId, inviteCode,
  planGrant, isFounding,
  signupSource, referrerId,
}) {
  if (!supabase) return { user: null, error: 'Supabase not configured' };
  try {
    // Validate invite code BEFORE creating the account (if invite-only mode)
    if (inviteCode) {
      const { data: checkData } = await supabase
        .from('invitations')
        .select('id, used_count, max_uses, expires_at, is_founding')
        .eq('code', inviteCode.trim())
        .maybeSingle();

      if (!checkData || checkData.used_count >= checkData.max_uses) {
        return { user: null, error: 'invite.invalid_code' };
      }
      if (checkData.expires_at && new Date(checkData.expires_at) < new Date()) {
        return { user: null, error: 'invite.expired_code' };
      }
    }

    const { data: authData, error: authErr } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
      },
    });
    if (authErr) return { user: null, error: mapError(authErr.message) };

    const uid = authData.user?.id;
    if (!uid) return { user: null, error: 'auth.error_unknown' };

    // Deterministic referral code from uid
    const referral_code = 'BEE' + Array.from({ length: 5 }, (_, i) =>
      'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[
        (uid.charCodeAt(i % uid.length) + i * 7) % 32
      ]
    ).join('');

    // Resolve signup_source — explicit param wins; fall back to inferring from inviteCode.
    const resolvedSource = signupSource || (inviteCode ? 'invite_code' : 'direct');

    const NET_META = {
      'ca_montreal': { city: 'Montréal', country: 'CA' },
      'fr_paris':    { city: 'Paris',    country: 'FR' },
      'fr_lorient':  { city: 'Lorient',  country: 'FR' },
    };
    const net = NET_META[networkId] || NET_META['ca_montreal'];

    // Update user_core (network_id lives in user_profiles, not here)
    await dbUpdate.userCore(uid, {
      name,
      age:                parseInt(age) || 18,
      dob_day:            parseInt(dobDay)   || null,
      dob_month:          parseInt(dobMonth) || null,
      dob_year:           parseInt(dobYear)  || null,
      gender,
      seeking:            Array.isArray(seeking) ? seeking : [seeking],
      referral_code,
      invites_remaining:  25,
      signup_source:      resolvedSource,
      // invited_by is normally set by use_invitation_code() for invite flows.
      // For friend_rating (or other non-code flows), set it here from referrerId.
      ...(referrerId && !inviteCode ? { invited_by: referrerId } : {}),
      ...(planGrant   ? { plan: planGrant }                  : {}),
      ...(isFounding  ? { is_founding_member: true }         : {}),
    });

    // Upsert (not just update) so this works even if the trigger didn't create the row yet
    await supabase?.from('user_profiles').upsert({
      user_id:    uid,
      network_id: networkId || 'ca_montreal',
      city:       net.city,
      country:    net.country,
    }, { onConflict: 'user_id' });

    // Consume the invite code (marks user as founding if applicable)
    if (inviteCode) {
      await supabase.rpc('use_invitation_code', {
        p_code:    inviteCode.trim(),
        p_user_id: uid,
      });
    }

    // Generate this user's personal invite codes.
    // Founding-phase: 25 per member to fuel viral growth before paid launch.
    await supabase.rpc('generate_invite_codes', {
      p_user_id:  uid,
      p_count:    25,
      p_founding: false,
    });

    // Welcome notification — first thing the user sees in their inbox.
    // Body adapts to whether friend rating is currently enabled, so we never
    // ask new users to do something the platform doesn't support right now.
    try {
      const isFr = (networkId === 'ca_montreal' || networkId === 'fr_lorient' || networkId === 'fr_paris' || networkId === 'be_brussels');
      const firstName = (name || '').split(' ')[0] || (isFr ? 'toi' : 'you');
      const isFromRating = signupSource === 'friend_rating';
      const { data: ratingFlag } = await supabase
        .from('app_features').select('enabled').eq('key', 'friend_rating').maybeSingle();
      const ratingOn = ratingFlag?.enabled !== false;
      const body = isFr
        ? (ratingOn
            ? (isFromRating
                ? "Tu es Founding Member à vie. Première mission : envoie ton lien à 5 amis pour activer ton radar de compatibilité."
                : "Tu es Founding Member à vie — accès complet, aucun trial, badge permanent. Première étape : invite tes amis à répondre au quiz sur toi.")
            : "Tu es Founding Member à vie — accès complet, aucun trial, badge permanent. Bonne découverte ✦")
        : (ratingOn
            ? (isFromRating
                ? "You're a Founding Member for life. First mission: send your link to 5 friends to activate your compatibility radar."
                : "You're a Founding Member for life — full access, no trial, permanent badge. First step: invite friends to answer the quiz about you.")
            : "You're a Founding Member for life — full access, no trial, permanent badge. Welcome aboard ✦");
      await supabase.from('notifications').insert({
        user_id: uid,
        type: 'system',
        title: isFr ? `Bienvenue dans la Ruche, ${firstName} 🐝` : `Welcome to the Hive, ${firstName} 🐝`,
        body,
        data: { source: 'welcome', signup_source: signupSource || 'direct' },
        read: false,
      });
    } catch { /* non-critical */ }

    return { user: authData.user, error: null };
  } catch (err) {
    return { user: null, error: err.message };
  }
}

// ── Sign in ───────────────────────────────────────────────────────────────
export async function loginUser({ email, password }) {
  if (!supabase) return { user: null, error: 'Supabase not configured' };
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { user: null, error: mapError(error.message) };
    return { user: data.user, error: null };
  } catch (err) {
    return { user: null, error: err.message };
  }
}

// ── Sign out ──────────────────────────────────────────────────────────────
export async function logoutUser() {
  if (!supabase) return;
  // Update last_active before signing out
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.id) {
      await dbUpdate.userCore(user.id, { last_active: new Date().toISOString() });
    }
  } catch {}
  await supabase.auth.signOut();
}

// ── Password reset email ──────────────────────────────────────────────────
export async function sendPasswordReset(email) {
  if (!supabase) return { error: 'Supabase not configured' };
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/#reset-password`,
  });
  return { error: error?.message || null };
}

// ── Update password (from reset link) ────────────────────────────────────
export async function updatePassword(newPassword) {
  if (!supabase) return { error: 'Supabase not configured' };
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error: error?.message || null };
}

// ── Load full user profile (user_core + user_profiles + user_scores) ──────
export async function loadUserProfile(uid) {
  if (!supabase || !uid) return { profile: null };
  try {
    const { data, error } = await db.users()
      ?.select('*')
      .eq('id', uid)
      .single();
    if (error || !data) return { profile: null };
    return { profile: data };
  } catch {
    return { profile: null };
  }
}

// ── Subscribe to auth state changes ──────────────────────────────────────
export function subscribeAuthState(callback) {
  if (!supabase) return () => {};
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        callback(session?.user || null);
      } else if (event === 'SIGNED_OUT') {
        callback(null);
      } else if (event === 'TOKEN_REFRESH_FAILED') {
        // Stale refresh token (e.g. after db reset) — clear session and redirect to login
        await supabase.auth.signOut();
        callback(null);
      } else if (event === 'PASSWORD_RECOVERY') {
        callback(session?.user || null);
      }
    }
  );
  return () => subscription?.unsubscribe();
}

// ── FNBC-aware profile update ─────────────────────────────────────────────
// Routes fields to the correct sub-table
const CORE_FIELDS = new Set([
  'email','name','age','dob_day','dob_month','dob_year',
  'gender','seeking','role','app_mode','mood','invite_count',
  'verified','email_verified','phone_verified','phone','fcm_token',
  'plan','premium','stripe_customer_id','referral_code','last_active','deleted_at',
  'incognito','paused','came_back_at','available_until',
  'hive_boost_group','hive_boost_until','founding_commit_at',
  'is_founding_member','signup_source','invited_by','invites_remaining',
]);
const SCORE_FIELDS = new Set([
  'quiz_answers','criteria_scores','friend_ratings',
  'score','votes','hive_score','plan_expires_at',
]);

export async function updateUserProfile(uid, fields) {
  const coreFields = {}, profileFields = {}, scoreFields = {};
  for (const [k, v] of Object.entries(fields)) {
    if (CORE_FIELDS.has(k))   coreFields[k]    = v;
    else if (SCORE_FIELDS.has(k)) scoreFields[k] = v;
    else                      profileFields[k]  = v;
  }
  const updates = [];
  if (Object.keys(coreFields).length    > 0) updates.push(dbUpdate.userCore(uid, coreFields));
  if (Object.keys(profileFields).length > 0) updates.push(dbUpdate.userProfile(uid, profileFields));
  if (Object.keys(scoreFields).length   > 0) updates.push(dbUpdate.userScores(uid, scoreFields));
  const results = await Promise.all(updates);
  const err = results.find(r => r?.error);
  return { error: err?.error?.message || null };
}

// ── Soft delete account ───────────────────────────────────────────────────
export async function deleteAccount(uid) {
  if (!supabase || !uid) return;
  await dbUpdate.userCore(uid, {
    deleted_at: new Date().toISOString(),
    email: `deleted_${uid}@beelove.invalid`,
  });
  await supabase.auth.signOut();
}

// ── Magic Link (passwordless email) ──────────────────────────────────────
// Sends a one-click login link to existing users only.
// On click, Supabase sets the session in the URL hash; onAuthStateChange fires automatically.
export async function sendMagicLink(email) {
  if (!supabase) return { error: 'Supabase not configured' };
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: {
      shouldCreateUser: false,
      emailRedirectTo: `${window.location.origin}/`,
    },
  });
  return { error: error?.message || null };
}

// ── Check if an email is registered (safe for anon) ───────────────────────
export async function checkEmailRegistered(email) {
  if (!supabase) return { exists: false, error: 'Supabase not configured' };
  const { data, error } = await supabase.rpc('check_email_registered', {
    p_email: email.trim().toLowerCase(),
  });
  return { exists: !!data, error: error?.message || null };
}

// ── OAuth — Google, Apple, Facebook ──────────────────────────────────────
// Requires Supabase Dashboard → Auth → Providers → enable each one
// And set redirect URL to: https://beelove.me/#auth/callback

export async function signInWithGoogle() {
  if (!supabase) return { error: 'Supabase not configured' };
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo:    `${window.location.origin}/#auth/callback`,
      queryParams:   { access_type: 'offline', prompt: 'consent' },
    },
  });
  if (error) return { error: error.message };
  // Supabase redirects automatically — no need to handle data here
  return { error: null };
}

export async function signInWithApple() {
  if (!supabase) return { error: 'Supabase not configured' };
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: `${window.location.origin}/#auth/callback`,
    },
  });
  if (error) return { error: error.message };
  return { error: null };
}

export async function signInWithFacebook() {
  if (!supabase) return { error: 'Supabase not configured' };
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'facebook',
    options: {
      redirectTo: `${window.location.origin}/#auth/callback`,
      scopes: 'email,public_profile',
    },
  });
  if (error) return { error: error.message };
  return { error: null };
}

// ── Handle OAuth callback (call on app load when hash = #auth/callback) ──
// Supabase sets the session automatically from the URL fragment.
// This function just confirms the session is established.
export async function handleOAuthCallback() {
  if (!supabase) return { user: null, error: 'Supabase not configured' };
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session) return { user: null, error: error?.message || 'No session' };

  const uid  = session.user.id;
  const meta = session.user.user_metadata || {};

  // Check if profile is complete (gender = first-time OAuth user indicator)
  const { data: existing } = await db.users()?.select('id,gender').eq('id', uid).single();
  const isNew = !existing || !existing.gender;

  if (!existing) {
    // Trigger may not have fired yet — upsert the row from OAuth metadata.
    // UPDATE would silently affect 0 rows; upsert ensures the user always has a profile.
    await supabase.from('user_core').upsert({
      id:             uid,
      name:           meta.full_name || meta.name || session.user.email?.split('@')[0] || 'New user',
      email:          session.user.email || '',
      verified:       true,
      email_verified: true,
    }, { onConflict: 'id' });
    await supabase.from('user_profiles').upsert({ user_id: uid }, { onConflict: 'user_id' });
    await supabase.from('user_scores').upsert({ user_id: uid }, { onConflict: 'user_id' });
  }

  return { user: session.user, isNew, error: null };
}
